// Fase 25B: cliente HTTP del control plane (Super Admin), SEPARADO del cliente tenant. No dispara el
// refresh ni los eventos de sesion tenant. Usa la cookie de plataforma (credentials: include). El
// backend es autoritativo; aqui solo se presenta.
import type { BetaCommercial, BetaFunnelSummary, BetaJourney } from './betaFunnel';

const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3000').replace(/\/$/, '');

export class PlatformApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly code?: string, public readonly details?: unknown) {
    super(message);
    this.name = 'PlatformApiError';
  }
}

async function req<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, { ...options, credentials: 'include', headers });
  } catch {
    throw new PlatformApiError(0, 'No fue posible conectar con el servidor.');
  }
  const text = await res.text();
  const payload = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const p = (payload ?? {}) as { message?: string; error?: string; details?: unknown };
    throw new PlatformApiError(res.status, p.message || 'Ocurrió un error.', p.error, p.details);
  }
  return payload as T;
}

// --- Tipos ---
export type Lifecycle = 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
export interface PlatformUser { id: string; email: string; displayName: string; role: 'SUPER_ADMIN'; status: 'ACTIVE' | 'DISABLED' }
export interface UsageLimit { used: number; limit: number | null; unlimited: boolean; overLimit: boolean; remaining: number | null }
export interface Summary { total: number; active: number; suspended: number; archived: number }
export interface PlanOption { code: string; name: string; description: string | null }
export interface BusinessRow {
  id: string; name: string; slug: string; lifecycle: Lifecycle;
  planCode: string | null; planName: string | null; planMissing: boolean;
  stores: UsageLimit | null; users: UsageLimit | null; overLimit: boolean; createdAt: string;
}
export interface BusinessListResponse { businesses: BusinessRow[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }
export interface EntitlementView { key: string; label: string; kind: 'INTEGER' | 'BOOLEAN'; unlimited: boolean; limit: number | null; enabled: boolean | null }
export interface AuditEvent { id: string; action: string; reason: string | null; metadata: Record<string, unknown> | null; createdAt: string; actorName: string; actorEmail: string }
export interface BusinessDetail {
  id: string; name: string; slug: string; lifecycle: Lifecycle; createdAt: string; planMissing: boolean;
  plan: { code: string; name: string; effectiveFrom: string } | null;
  usage: { plan: { code: string; name: string }; stores: UsageLimit; users: UsageLimit; entitlements: EntitlementView[] } | null;
  stores: Array<{ id: string; name: string; isActive: boolean; timezone: string | null }>;
  users: Array<{ id: string; name: string; email: string; role: string; isActive: boolean }>;
  integrations: { loyverseConnected: boolean };
  audit: AuditEvent[];
}

// --- Auth ---
export const platformLogin = (email: string, password: string) => req<{ user: PlatformUser }>('/api/platform/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
export const platformMe = () => req<{ user: PlatformUser }>('/api/platform/auth/me');
export const platformLogout = () => req<{ ok: boolean }>('/api/platform/auth/logout', { method: 'POST', body: JSON.stringify({}) });

// --- Data ---
export const getSummary = () => req<Summary>('/api/platform/summary');
export const getPlans = () => req<{ plans: PlanOption[] }>('/api/platform/plans');
export interface BusinessListFilters { search?: string; lifecycle?: Lifecycle; planCode?: string; overLimit?: boolean; limit?: number; offset?: number }
export function listBusinesses(f: BusinessListFilters) {
  const q = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
  // Sin `URLSearchParams.size`: no existe en Chromium 83, el WebView de Android 11 (Bloque G).
  const search = q.toString();
  return req<BusinessListResponse>(`/api/platform/businesses${search ? `?${search}` : ''}`);
}
export const getBusiness = (id: string) => req<BusinessDetail>(`/api/platform/businesses/${id}`);
export const changeLifecycle = (id: string, status: Lifecycle, reason?: string) => req<BusinessDetail>(`/api/platform/businesses/${id}/lifecycle`, { method: 'POST', body: JSON.stringify({ status, ...(reason ? { reason } : {}) }) });
export const changePlan = (id: string, planCode: string, reason: string) => req<BusinessDetail>(`/api/platform/businesses/${id}/plan`, { method: 'POST', body: JSON.stringify({ planCode, reason }) });
export interface CreateBusinessBody { businessName: string; businessSlug: string; planCode: string; owner: { firstName: string; lastName: string; email: string; username: string; phone?: string; password: string } }
export const createBusiness = (body: CreateBusinessBody) => req<BusinessDetail>('/api/platform/businesses', { method: 'POST', body: JSON.stringify(body) });

// --- ONB-010: invitaciones a la beta privada (el token del enlace nunca llega aquí) ---
export type InvitationState = 'PENDING' | 'EXPIRED' | 'ACCEPTED' | 'ACCEPTED_EXPIRED' | 'PROVISIONED' | 'REVOKED';
export interface BetaInvitation {
  id: string; email: string; note: string | null; status: string; state: InvitationState;
  expiresAt: string; createdAt: string; sendCount: number; lastSentAt: string | null;
  acceptedAt: string | null; provisionedAt: string | null; revokedAt: string | null;
  invitedBy: string | null; business: { id: string; name: string | null } | null;
  canResend: boolean; canRevoke: boolean;
  // P-085 (aditivos): el recorrido del tester y su condición comercial.
  journey?: BetaJourney; commercial?: BetaCommercial | null;
}
export interface BetaInvitationList {
  enabled: boolean; emailDeliveryConfigured: boolean; invitations: BetaInvitation[];
  // P-085 (aditivos): la beta cerrada a testers nuevos, el tope de la lista y el resumen del embudo de esa misma cohorte.
  closedToNew?: boolean; listLimit?: number; summary?: BetaFunnelSummary;
}
export const listBetaInvitations = () => req<BetaInvitationList>('/api/platform/beta-invitations');
// `confirmExistingAccount`: ese correo ya entra a otro negocio y se confirma invitarlo de todos modos (409 si no).
export const createBetaInvitation = (email: string, note?: string, confirmExistingAccount = false) =>
  req<BetaInvitation>('/api/platform/beta-invitations', { method: 'POST', body: JSON.stringify({ email, ...(note ? { note } : {}), ...(confirmExistingAccount ? { confirmExistingAccount: true } : {}) }) });
export const resendBetaInvitation = (id: string) => req<BetaInvitation>(`/api/platform/beta-invitations/${id}/resend`, { method: 'POST', body: JSON.stringify({}) });
export const revokeBetaInvitation = (id: string, reason: string) => req<BetaInvitation>(`/api/platform/beta-invitations/${id}/revoke`, { method: 'POST', body: JSON.stringify({ reason }) });
